/**
 * warehouseApi.js
 * ───────────────
 * Client for the warehouse read endpoints (Phase 3a — read only).
 *   getWarehouseItems       → list (current-occupancy default + optional filters)
 *   getWarehouseItemDetail  → single item (full doc incl. stays[] + dwell_days)
 *
 * caller_email rides the query string (URLSearchParams auto-encodes '#' → '%23');
 * site_id + filters ride the POST body — exactly the contract get_warehouse_items
 * expects (caller_email from req.params, everything else from the JSON body).
 */

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

async function postWarehouse(callerEmail, body) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const response = await apiFetch(`${API_BASE_URL}/get_warehouse_items?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`get_warehouse_items error: ${response.status}`);
  return response.json();
}

/**
 * List warehouse items for a site. Defaults to the backend current-occupancy set
 * (STORED / STAGED_INBOUND / STAGED_AT_WAREHOUSE / ON_LOAN) — pass no warehouse_status
 * override here so that default stands. `filters` is forwarded as-is to the body.
 * Returns the items array (each item carries dwell_days + full stays[]).
 */
export async function getWarehouseItems(callerEmail, { siteId, filters = {} } = {}) {
  if (!siteId) return [];
  const data = await postWarehouse(callerEmail, { site_id: siteId, ...filters });
  return Array.isArray(data.items) ? data.items : [];
}

/**
 * Detail-by-id. Returns the full warehouse_item doc (root + stays[] + derived
 * dwell_days), or null if not found / out of scope.
 */
export async function getWarehouseItemDetail(callerEmail, { siteId, warehouseItemId }) {
  if (!siteId || !warehouseItemId) return null;
  const data = await postWarehouse(callerEmail, {
    site_id: siteId,
    warehouse_item_id: warehouseItemId,
  });
  return data.warehouse_item || null;
}

/**
 * Search tools at a site (for the ATTACH picker), backed by the canonical
 * search_inventory endpoint. GET — caller_email + site_id + q on the query
 * string (URLSearchParams auto-encodes '#' → '%23' like the sibling calls).
 * search_inventory returns a BARE array (not {results}); we hand it back as-is.
 * Returns [] for q shorter than 2 chars (matches the endpoint's own threshold).
 */
export async function searchToolsForAttach(callerEmail, siteId, q) {
  const query = (q || '').trim();
  if (!siteId || query.length < 2) return [];
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  params.append('site_id', siteId);
  params.append('q', query);
  const response = await apiFetch(`${API_BASE_URL}/search_inventory?${params.toString()}`);
  if (!response.ok) throw new Error(`search_inventory error: ${response.status}`);
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Receive (create) a warehouse item → opens a stay. POSTs
 * manage_warehouse_item {action:'receive', …payload}. caller_email rides the
 * query string (auto %23); everything else rides the body. Resolves to
 * { warehouse_item, spawned_tool? } on 201; throws an Error carrying .status
 * and the backend's error message on 4xx/5xx (caller surfaces it inline).
 */
export async function receiveWarehouseItem(callerEmail, payload) {
  return manageWarehouse(callerEmail, { action: 'receive', ...payload });
}

/* ── Item actions (3b-ii) — each POSTs manage_warehouse_item {action,…} ───────
   caller_email on the query string (auto %23); item identity + fields in body.
   Throws Error with .status + backend message on 4xx/5xx so the panel can
   surface preconditions (e.g. ship's 409 on an unverified item) inline. */
async function manageWarehouse(callerEmail, body) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const response = await apiFetch(`${API_BASE_URL}/manage_warehouse_item?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `Action failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return data;
}

export function setVerified(callerEmail, { siteId, warehouseItemId }) {
  return manageWarehouse(callerEmail, {
    action: 'set_verified', site_id: siteId, warehouse_item_id: warehouseItemId, verified: true,
  });
}

export function setHold(callerEmail, { siteId, warehouseItemId, hold, holdReason }) {
  return manageWarehouse(callerEmail, {
    action: 'set_hold', site_id: siteId, warehouse_item_id: warehouseItemId,
    hold, ...(holdReason ? { hold_reason: holdReason } : {}),
  });
}

export function putAwayWarehouseItem(callerEmail, { siteId, warehouseItemId, location }) {
  return manageWarehouse(callerEmail, {
    action: 'put_away', site_id: siteId, warehouse_item_id: warehouseItemId, ...location,
  });
}

export function moveWarehouseItem(callerEmail, { siteId, warehouseItemId, location }) {
  return manageWarehouse(callerEmail, {
    action: 'move', site_id: siteId, warehouse_item_id: warehouseItemId, ...location,
  });
}

export function shipWarehouseItem(callerEmail, { siteId, warehouseItemId, shipDisposition, shippedDate, shipRef }) {
  return manageWarehouse(callerEmail, {
    action: 'ship', site_id: siteId, warehouse_item_id: warehouseItemId,
    ship_disposition: shipDisposition, shipped_date: shippedDate,
    ...(shipRef ? { ship_ref: shipRef } : {}),
  });
}

/* Batch-stamp dwell_disposition_status on warehouse items at ONE storage site.
   Stamps by WAREHOUSE_ITEM_ID (not tool id) — single-site, point-read per item on
   site_id's partition (see _action_set_disposition). Each updated item leaves an
   explicit ChangeLog entry. Resolves { updated:[ids], skipped:[{id,reason}] }. */
export function setDisposition(callerEmail, { siteId, warehouseItemIds, dispositionStatus }) {
  return manageWarehouse(callerEmail, {
    action: 'set_disposition', site_id: siteId,
    warehouse_item_ids: warehouseItemIds, disposition_status: dispositionStatus,
  });
}
